// src/components/page/ar-space/MoreBtn.tsx
'use client';

import { useEffect, useMemo, useState, ReactNode } from 'react';
import { IconMore } from '@/components/icons';
import { useRouter } from 'next/navigation';
import DropDownMenu from '@/components/ui/dropDownMenu/DropDownMenu';
import type { ArSpace, ArSpaceMixDetail } from '@/types/arSpace';
import ShareModal from '@/components/ui/modal/ShareModal';
import ReportModal from '@/components/ui/modal/ReportModal';
import IconModal from '@/components/ui/modal/IconModal';
import { useAppSelector } from '@/store/hook';
import { ArSpaceService } from '@/libs/services/arSpace';
import { showApiError } from '@/libs/helpers/showApiState';

// ---- 可能從 meInfo 拿到的 ID 欄位 ----
type MeInfoLike = {
  womo_id?: string;
  womoId?: string;
  user_id?: string | number;
  userId?: string | number;
  owner_id?: string | number;
  owner_open_id?: string;
  store_id?: string | number;
  storeId?: string | number;
  kol_id?: string | number;
  kolId?: string | number;
};

// 讓 MoreBtn 可以吃「列表的 ArSpace」或「內頁的 ArSpaceMixDetail」
type ArSpaceLike = ArSpace | ArSpaceMixDetail;

// 從空間資料裡收集「所有可能代表擁有者的 ID」
function collectOwnerIdsFromArSpace(space?: ArSpaceLike): string[] {
  if (!space) return [];
  const ids: (string | number | undefined)[] = [];

  // 列表型別（/ar_space/mix）
  if ('owner_open_id' in space) ids.push(space.owner_open_id);
  if ('owner_id' in space) ids.push(space.owner_id);

  // 個人 AR 內頁（ArSpaceUserDetail）
  if ('womo_id' in space) ids.push(space.womo_id);
  if ('user_id' in space) ids.push(space.user_id);

  // 商家 AR 內頁（ArSpaceStoreDetail）
  if ('store_id' in space) ids.push(space.store_id);

  return ids.filter((v) => v != null).map((v) => String(v));
}

type MoreBtnProps = {
  arSpace?: ArSpaceLike;
  /** 刪除成功時給父層通知（例如列表裡直接把該卡片 filter 掉） */
  onDeleted?: (arSpaceId: string | number) => void;
  showShare?: boolean;
  showFavor?: boolean;
  showReport?: boolean;
  className?: string;
  triggerIcon?: ReactNode;
};

// Header 的功能鍵（更多選單 + 內建 ShareModal）
export const MoreBtn = ({
  arSpace,
  onDeleted,
  showShare = true,
  showFavor = true,
  showReport = true,
  className,
  triggerIcon,
}: MoreBtnProps) => {
  const router = useRouter();
  const meInfo = useAppSelector((state) => state.auth.meInfo) as MeInfoLike | null;

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const [openShare, setOpenShare] = useState(false);
  const [openReport, setOpenReport] = useState(false);

  // 刪除確認 Modal
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  // 刪除成功的通用 Modal
  const [modalType, setModalType] = useState<'success' | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // 刪除後要做什麼動作
  const [afterDeleteAction, setAfterDeleteAction] = useState<
    'detail-navigate' | 'list-refresh' | null
  >(null);

  const [shareLoading, setShareLoading] = useState(false);

  const resolvedIcon = useMemo(() => {
    return triggerIcon ?? <IconMore width={23} className="text-grey" />;
  }, [triggerIcon]);

  // 空間擁有者的所有 ID（字串）
  const spaceOwnerIds = useMemo(() => collectOwnerIdsFromArSpace(arSpace), [arSpace]);

  // ========= 是否為本人（用多種欄位比對） =========
  const isOwner = useMemo(() => {
    if (!meInfo || !arSpace) return false;

    const meIds = [
      meInfo.owner_open_id,
      meInfo.womo_id,
      meInfo.womoId,
      meInfo.user_id,
      meInfo.userId,
      meInfo.owner_id,
      meInfo.store_id,
      meInfo.storeId,
      meInfo.kol_id,
      meInfo.kolId,
    ]
      .filter((v) => v != null)
      .map((v) => String(v));

    if (!meIds.length || !spaceOwnerIds.length) return false;

    const matched = spaceOwnerIds.some((id) => meIds.includes(id));

    if (process.env.NODE_ENV !== 'production') {
      // debug 用：你可以在 Console 看看實際比對的值
      // eslint-disable-next-line no-console
      // console.log('[MoreBtn] owner check', {
      //   meInfo,
      //   meIds,
      //   arSpace,
      //   spaceOwnerIds,
      //   matched,
      // });
    }

    return matched;
  }, [meInfo, arSpace, spaceOwnerIds]);

  // 只有「本人」才可以編輯／刪除
  const canEdit = isOwner;

  // 這裡是給 DropDownMenu 判斷的 owner ID（拿空間 ownerIds 的第一個）
  const ownerKolIdForMenu = spaceOwnerIds[0];

  // ========= 分享用 URL =========
  const shareUrl = useMemo(() => {
    if (typeof window !== 'undefined') {
      if (arSpace?.id) {
        // 依照當前 origin 組絕對網址（local / prod 都 OK）
        return new URL(
          `/ar-space/${arSpace.id}/?type=${arSpace.ar_type}`,
          window.location.origin
        ).toString();
      }
      return window.location.href;
    }
    // SSR fallback：給個正式網域（請依你的正式 domain 調整）
    return arSpace?.id
      ? `https://www.womo.com.tw/ar-space/${arSpace.id}/?type=${arSpace.ar_type}`
      : '';
  }, [arSpace?.id, arSpace?.ar_type]);

  // ========= 分享用 Title =========
  const shareTitle = useMemo(() => {
    if (arSpace?.title && arSpace.title.trim()) return arSpace.title.trim();
    return 'WOMO 的 AR 空間';
  }, [arSpace?.title]);

  // ========= 分享用 Description =========
  const shareDesc = useMemo(() => {
    if (!arSpace?.title) return '';
    return arSpace.title
      .replace(/<[^>]+>/g, ' ') // 去掉 HTML tag
      .replace(/\s+/g, ' ')
      .slice(0, 160);
  }, [arSpace?.title]);

  // 這裡才是「真正的 id」（字串）
  const arSpaceId = arSpace?.id ?? '';

  // ========= 編輯 =========
  const handleEdit = () => {
    if (!arSpace || !arSpace.id) return;
    router.push(`/me/ar-space/${arSpace.id}/edit`); // 之後再依實際路徑調整
  };

  // ========= 刪除：先打開確認 IconModal（warning） =========
  const handleDelete = () => {
    if (!arSpace || !arSpace.id) return;
    setOpenDeleteConfirm(true);
  };

  // ========= 確認刪除：真正打 API =========
  const handleConfirmDelete = async () => {
    if (!arSpace || !arSpace.id) return;
    if (deleteLoading) return;

    const id = arSpace.id;

    try {
      setDeleteLoading(true);

      await ArSpaceService.deleteUnitArSpace(id);

      // ---------------------------
      // 刪除成功後的行為：
      // 1. 如果父層有傳 onDeleted => 交給父層處理（不做導頁 / refresh）
      // 2. 否則依照目前路徑決定 detail-navigate / list-refresh
      // ---------------------------
      if (onDeleted) {
        onDeleted(id);
        setAfterDeleteAction(null); // ✅ 列表模式：不做 router.refresh
      } else if (typeof window !== 'undefined') {
        const path = window.location.pathname;

        if (/^\/ar-space\/[^/]+/.test(path)) {
          setAfterDeleteAction('detail-navigate');
        } else {
          // 這裡保留給之後真的需要重抓列表的情境
          setAfterDeleteAction('list-refresh');
        }
      }

      setOpenDeleteConfirm(false);
      setModalType('success');
    } catch (err) {
      console.error('[ArSpaceHeaderFuncBtn] delete ar-space error:', err);
      showApiError(err, '刪除 AR 空間失敗');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ========= 分享：目前先只開 shareModal，不打 API =========
  const handleShare = async () => {
    if (shareLoading) return;

    // 若沒有 arSpaceId，就只打開分享 Modal
    if (!arSpaceId) {
      setOpenShare(true);
      return;
    }

    // 之後如果有 /ar_space/share 類似 API，可以在這裡串接
    setOpenShare(true);
  };

  // ========= 刪除成功後，成功 modal 關閉時要做的導頁/刷新 =========
  const handleAfterDelete = () => {
    const action = afterDeleteAction;

    setModalType(null);
    setAfterDeleteAction(null);

    if (!action) return;

    if (action === 'detail-navigate') {
      if (typeof window !== 'undefined' && window.history.length > 1) {
        router.back();
      } else {
        router.replace('/ar-space');
      }
    } else if (action === 'list-refresh') {
      // ⚠️ 現在列表模式會優先走 onDeleted，所以這裡只當備用邏輯；
      // 若不想任何重整，可以直接把整個 block 移除或註解掉。
      if (typeof window !== 'undefined') {
        const r = router as unknown as { refresh?: () => void };
        if (typeof r.refresh === 'function') {
          r.refresh();
        } else {
          window.location.reload();
        }
      }
    }
  };

  return (
    <>
      {/* 只有在客戶端掛載後才渲染 DropDownMenu（避免 SSR / hydration issue） */}
      {isMounted && (
        <DropDownMenu
          trigger={
            <button type="button" disabled={shareLoading}>
              {resolvedIcon}
            </button>
          }
          // 判斷是否本人貼文
          ownerKolId={ownerKolIdForMenu}
          // 判斷是否能編輯（本人 + 有登入）
          canEdit={canEdit}
          // 編輯 / 刪除：本人 + canEdit 才會顯示
          onEdit={handleEdit}
          onDelete={handleDelete}
          // 點「分享」→ 打開內建 ShareModal
          onShare={handleShare}
          //onReport={() => setOpenReport(true)}
          showShare={showShare}
          showFavor={showFavor}
          showReport={showReport}
          className={className}
        />
      )}

      {/* 自家分享 Modal：把 url / title / desc 都丟進去 */}
      <ShareModal
        open={openShare}
        onClose={() => setOpenShare(false)}
        title={`分享 ${shareTitle} AR空間`}
        url={shareUrl || undefined}
        shareTitle={shareTitle}
        shareDescription={shareDesc || undefined}
      />

      <ReportModal open={openReport} onClose={() => setOpenReport(false)} />

      {/* 刪除確認的提示 IconModal（warning） */}
      <IconModal
        show={openDeleteConfirm}
        variant="warning"
        size="md"
        title="是否要刪除此 AR 空間？"
        onClose={() => {
          if (deleteLoading) return;
          setOpenDeleteConfirm(false);
        }}
        onConfirm={handleConfirmDelete}
      >
        刪除後即無法恢復動作。
      </IconModal>

      {/* 刪除成功的提示 IconModal（通用 success modal） */}
      <IconModal
        show={modalType === 'success'}
        variant="success"
        size="md"
        title="已成功刪除 AR 空間！"
        autoCloseSeconds={2}
        onClose={handleAfterDelete}
      />
    </>
  );
};
